'use client'

import { useGetWorkflowQuery } from '@/lib/features/workflow/workflowApi'
import { cn } from '@/lib/utils'
import { formatWorkflowName } from '@/utils/workflowUtils'
import { motion } from 'framer-motion'
import { X } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { WorkflowRefStrict } from 'sprites-types/api/v2/workflows'

type ContextItem = {
  id: string
  type: 'node' | 'workflow' | 'execution'
  label: string
}

interface ContextDisplayProps {
  selectedNodeIds?: string[]
  nodeId?: string
  selectedExecutionIds?: string[]
  workflowRef?: WorkflowRefStrict
  onRemoveNode?: (nodeId: string) => void
  onRemoveExecution?: (executionId: string) => void
  showRemoveButtons?: boolean
  animated?: boolean
  initialWidth?: number
  truncateText?: boolean
}

export default function ContextDisplay({
  selectedNodeIds = [],
  nodeId,
  selectedExecutionIds = [],
  workflowRef,
  onRemoveNode,
  onRemoveExecution,
  showRemoveButtons = false,
  animated = false,
  initialWidth,
  truncateText = false,
}: ContextDisplayProps) {
  const contextItems: ContextItem[] = []
  const containerRef = useRef<HTMLDivElement>(null)
  const [calculatedMaxItems, setCalculatedMaxItems] = useState<number>(2)

  const { data: workflowData } = useGetWorkflowQuery(workflowRef as string, {
    skip: !workflowRef,
  })

  // Show selected nodes (use selectedNodeIds if available, fall back to nodeId)
  const nodesToShow = selectedNodeIds.length > 0 ? selectedNodeIds : nodeId ? [nodeId] : []

  nodesToShow.forEach((id) => {
    // Find the node in the workflow data to get its name
    const node = workflowData?.data.nodes.find((n) => n.id === id)
    const nodeName = node?.name || id // Fall back to ID if name not found

    contextItems.push({
      id,
      type: 'node',
      label: nodeName,
    })
  })

  // Show selected executions
  selectedExecutionIds.forEach((executionId) => {
    // Format execution ID for display (show first 8 characters)
    const displayId = executionId.startsWith('execution_')
      ? executionId.slice(10, 18) // Remove 'execution_' prefix and show first 8 chars
      : executionId.slice(0, 8)

    contextItems.push({
      id: executionId,
      type: 'execution',
      label: displayId,
    })
  })

  if (workflowRef) {
    contextItems.push({
      id: workflowRef,
      type: 'workflow',
      label: formatWorkflowName(workflowData?.metadata.name || workflowRef),
    })
  }

  // Measure container width and calculate how many items can fit
  useEffect(() => {
    const measureContainer = () => {
      if (containerRef.current) {
        const width = containerRef.current.offsetWidth

        // Estimate item width: ~150px per item + 4px gap
        const estimatedItemWidth = 154
        const overflowTagWidth = 60 // Width of the overflow tag
        const availableWidth = width - overflowTagWidth - 4 // Subtract overflow tag and gap

        const maxItems = Math.max(0, Math.floor(availableWidth / estimatedItemWidth))
        setCalculatedMaxItems(maxItems)
      }
    }

    // Only measure when we have items
    if (contextItems.length > 0) {
      measureContainer()
    }

    window.addEventListener('resize', measureContainer)
    return () => window.removeEventListener('resize', measureContainer)
  }, [contextItems.length])

  if (contextItems.length === 0) {
    return null
  }

  const content = (
    <div ref={containerRef} className="flex flex-wrap gap-1">
      {contextItems
        .slice(0, truncateText ? calculatedMaxItems : contextItems.length)
        .map((item) => (
          <div
            key={`${item.type}-${item.id}`}
            className={cn(
              'flex max-w-[200px] items-center gap-1 rounded-md px-2 py-1 text-xs',
              item.type === 'node'
                ? 'bg-blue-100 text-blue-800'
                : item.type === 'execution'
                  ? 'bg-purple-100 text-purple-800'
                  : 'bg-green-100 text-green-800'
            )}
          >
            <span className={`font-medium ${truncateText ? 'max-w-[60px] truncate' : 'truncate'}`}>
              {item.type === 'node' ? 'Node' : item.type === 'execution' ? 'Execution' : 'Workflow'}
              : {item.label}
            </span>
            {item.type === 'node' && showRemoveButtons && onRemoveNode && (
              <button
                onClick={() => onRemoveNode(item.id)}
                className="flex h-3 w-3 items-center justify-center rounded-full hover:bg-blue-200"
                aria-label={`Remove ${item.label} from context`}
              >
                <X size={8} />
              </button>
            )}
            {item.type === 'execution' && showRemoveButtons && onRemoveExecution && (
              <button
                onClick={() => onRemoveExecution(item.id)}
                className="flex h-3 w-3 items-center justify-center rounded-full hover:bg-purple-200"
                aria-label={`Remove ${item.label} from context`}
              >
                <X size={8} />
              </button>
            )}
          </div>
        ))}
      {truncateText && contextItems.length > calculatedMaxItems && (
        <div className="flex items-center gap-1 rounded-md bg-orange-100 px-2 py-1 text-xs text-orange-800">
          <span className="font-medium">...{contextItems.length - calculatedMaxItems}+</span>
        </div>
      )}
    </div>
  )

  if (animated) {
    return (
      <motion.div
        initial={{
          opacity: 0,
          height: 0,
          width: initialWidth || 'auto',
        }}
        animate={{
          opacity: 1,
          height: 'auto',
          width: 'auto',
        }}
        exit={{
          opacity: 0,
          height: 0,
          width: initialWidth || 'auto',
        }}
        transition={{ duration: 0.2, ease: 'easeOut' }}
        className="overflow-hidden"
      >
        {content}
      </motion.div>
    )
  }

  return content
}
